import React from 'react';
import { Grid, makeStyles, createStyles } from '@material-ui/core';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import '../../ui/controls/dateTimePicker.css';
import { BaseInput } from 'src/components/ui/form/BaseInput';
import { primaryText, black, primaryGreen, white, grey } from 'src/data/colors';
import { Header } from './DatePickerComponents';

export interface Props {
  start: Date | undefined;
  end: Date | undefined;
  maxRange?: number;
  filterDates?: (date: Date) => boolean;
  includeTime?: boolean;
  onStartChange: (date: Date) => void;
  onEndChange: (date: Date) => void;
  dateFormat?: string;
  disabled?: boolean;
}

const useStyles = makeStyles(() =>
  createStyles({
    item: {
      width: '50%',
    },
    datePicker: {
      fontFamily: '"DM Sans", sans-serif',
      width: '100%',
      maxWidth: '320px',
      '& .react-datepicker__header': {
        backgroundColor: white,
        borderBottom: 'none',
      },
      '& .react-datepicker__day-names': {
        '& .react-datepicker__day-name': {
          width: '36px',
          height: '36px',
        },
      },
      '& .react-datepicker__month-container': {
        width: '100%',
      },
      '& .react-datepicker__month': {
        margin: 0,
        padding: '0 16px',
      },
      '& .react-datepicker__day--disabled': {
        color: grey,
        '&:hover': {
          boxShadow: 'none',
        },
      },
      '& .react-datepicker__day--keyboard-selected': {
        backgroundColor: grey,
      },
      '& .react-datepicker__time-box': {
        '& .react-datepicker__time-list': {
          '& .react-datepicker__time-list-item': {
            height: 'auto !important',
            '&.react-datepicker__time-list-item--selected': {
              backgroundColor: `${primaryGreen} !important`,
              color: 'white',
            },
          },
        },
      },
    },
    day: {
      color: primaryText,
      borderRadius: '50%',
      boxSizing: 'border-box',
      width: '36px',
      height: '36px',
      textAlign: 'center',
      lineHeight: '36px',
      '&:hover': {
        boxSizing: 'border-box',
        borderRadius: '50%',
        backgroundColor: 'white',
        boxShadow: `0 0 0 1px ${black}`,
      },
      '&.react-datepicker__day--today': {
        fontWeight: 900,
        backgroundColor: 'transparent',
      },
      '&.react-datepicker__day--selected': {
        backgroundColor: primaryGreen,
        color: 'white',
      },
    },
  })
);

const daysToMS = (dayCount: number) => {
  return dayCount * 1000 * 60 * 60 * 24;
};

const DEFAULT_DATE_FORMAT = 'yyyy/MM/dd';
const DEFAULT_TIME_FORMAT = 'h:mm aa';
const DEFAULT_RANGE = 1000 * 60 * 60 * 24;

export const DateRange: React.FC<Props> = (props) => {
  const { start, end, onStartChange, onEndChange, maxRange } = props;
  const classes = useStyles();

  const handleStartDateChange = (newStart: Date) => {
    const currentRange = end && start ? end.getTime() - start.getTime() : undefined;
    if (maxRange && end && end.getTime() - newStart.getTime() > daysToMS(maxRange)) {
      onStartChange(newStart);
      onEndChange(new Date(newStart.getTime() + daysToMS(maxRange)));
    } else if (end && newStart > end) {
      onStartChange(newStart);
      onEndChange(new Date(newStart.getTime() + (currentRange || DEFAULT_RANGE)));
    } else {
      onStartChange(newStart);
    }
  };

  const handleEndDateChange = (newEnd: Date) => {
    const currentRange = end && start ? end.getTime() - start.getTime() : undefined;
    if (maxRange && start && newEnd.getTime() - start.getTime() > daysToMS(maxRange)) {
      onStartChange(new Date(newEnd.getTime() - daysToMS(maxRange)));
      onEndChange(newEnd);
    } else if (start && newEnd < start) {
      onStartChange(new Date(newEnd.getTime() - (currentRange || DEFAULT_RANGE)));
      onEndChange(newEnd);
    } else {
      onEndChange(newEnd);
    }
  };

  return (
    <Grid container spacing={2}>
      <Grid item className={classes.item}>
        <DatePicker
          calendarClassName={classes.datePicker}
          dayClassName={() => classes.day}
          disabled={props.disabled}
          selected={start}
          onChange={(e: Date) => e && handleStartDateChange(e)}
          dateFormat={DEFAULT_DATE_FORMAT}
          filterDate={props.filterDates}
          renderCustomHeader={({ date, customHeaderCount, decreaseMonth, increaseMonth }) => (
            <Header
              date={date}
              customHeaderCount={customHeaderCount}
              decreaseMonth={decreaseMonth}
              increaseMonth={increaseMonth}
            />
          )}
          customInput={
            <BaseInput
              size="small"
              InputLabelProps={{
                shrink: true,
              }}
              fullWidth
              label="Start Date"
              variant="outlined"
              placeholder="Start Date"
            />
          }
        />
      </Grid>

      <Grid item className={classes.item}>
        <DatePicker
          calendarClassName={classes.datePicker}
          disabled={props.disabled}
          selected={start}
          onChange={(e: Date) => e && onStartChange(e)}
          dateFormat={DEFAULT_TIME_FORMAT}
          filterDate={props.filterDates}
          showTimeSelect
          showTimeSelectOnly
          timeIntervals={15}
          customInput={
            <BaseInput
              size="small"
              InputLabelProps={{
                shrink: true,
              }}
              fullWidth
              label="Start Time"
              variant="outlined"
              placeholder="Start Time"
            />
          }
        />
      </Grid>

      <Grid item className={classes.item}>
        <DatePicker
          calendarClassName={classes.datePicker}
          dayClassName={() => classes.day}
          disabled={props.disabled}
          selected={end}
          onChange={(e: Date) => e && handleEndDateChange(e)}
          dateFormat={DEFAULT_DATE_FORMAT}
          filterDate={props.filterDates}
          renderCustomHeader={({ date, customHeaderCount, decreaseMonth, increaseMonth }) => (
            <Header
              date={date}
              customHeaderCount={customHeaderCount}
              decreaseMonth={decreaseMonth}
              increaseMonth={increaseMonth}
            />
          )}
          customInput={
            <BaseInput
              size="small"
              InputLabelProps={{
                shrink: true,
              }}
              fullWidth
              label="End Date"
              variant="outlined"
              placeholder="End Date"
            />
          }
        />
      </Grid>

      <Grid item className={classes.item}>
        <DatePicker
          calendarClassName={classes.datePicker}
          disabled={props.disabled}
          selected={end}
          onChange={(e: Date) => e && onEndChange(e)}
          dateFormat={DEFAULT_TIME_FORMAT}
          filterDate={props.filterDates}
          showTimeSelect
          showTimeSelectOnly
          timeIntervals={15}
          customInput={
            <BaseInput
              size="small"
              InputLabelProps={{
                shrink: true,
              }}
              fullWidth
              label="End Time"
              variant="outlined"
              placeholder="End Time"
            />
          }
        />
      </Grid>
    </Grid>
  );
};
